import { DeviceWidth, SearchRecommendation } from "../types";
import { minWidth } from "./utilities";
import {
  openSideMenu,
  SideMenuSettings,
} from "./components/side-menu/SideMenu";
import { CategoryMenu } from "./components/side-menu/CategoryMenu";
import { hideBackdrop, showBackdrop } from "./components/Backdrop";
import dialogPolyfill from "dialog-polyfill";
import { getSearchRecommendations } from "./api";
import { debounce, Logger, select } from "@fruugo/utilities";
import { SearchRecommendations } from "./components/SearchRecommendations";
import { fadeIn } from "./animations";
import { isInABTest } from "../modules/abTesting.js";
import { sendSearchEvent } from "./analytics/google-analytics";

const Log = new Logger(window.environment);

type Config = {
  searchBarClass: string;
  openSearchBarClass: string;
  closeSearchBarClass: string;
  searchBarInputClass: string;
  searchBarFormClass: string;
  openCategorySelectionClass: string;
  searchRecommendationsClass: string;
  debounceDelay: number;
  minLengthForSearch: number;
  minLengthForSearchRecommendations: number;
};

class SearchBarBase {
  config: Config;
  searchBar: HTMLDialogElement | null;
  searchBarInput: HTMLInputElement | null;
  debounceDelay = 280;

  constructor(config: Config) {
    this.config = config;
    this.searchBar = select<HTMLDialogElement>(config.searchBarClass);
    this.searchBarInput = select<HTMLInputElement>(
      this.config.searchBarInputClass
    );
    this.debounceDelay = config.debounceDelay;

    // Polyfill for older browsers like Safari 15.2
    if (this.searchBar && !this.searchBar.showModal) {
      dialogPolyfill.registerDialog(this.searchBar);
    }
  }

  isValidSearch = (query: string) =>
    query && query.length >= this.config.minLengthForSearch;

  handleFormSubmit = (event: Event) => {
    const form = event.target as HTMLFormElement;
    const data = new FormData(form);
    const query = data.get("q") as string;

    if (this.isValidSearch(query)) {
      sendSearchEvent(query);
    }
  };

  handleSearchRecommendations = debounce((event: Event) => {
    const target = event.target as HTMLInputElement;
    const query = target.value;

    const element = select<HTMLUListElement>(
      this.config.searchRecommendationsClass
    );

    const clearRecommendations = () => {
      element?.replaceChildren();
    };

    if (
      !query ||
      query.length < this.config.minLengthForSearchRecommendations
    ) {
      clearRecommendations();
      return;
    }

    const loading = () => {
      element?.replaceChildren(
        <li>
          <div
            class="skeleton-animate fadeOut mx-24 mb-8"
            style="height: 24px"
          ></div>
        </li>
      );

      const skeleton = element?.querySelector(
        ".skeleton-animate"
      ) as HTMLElement;

      skeleton && fadeIn(skeleton);
    };

    loading();

    getSearchRecommendations(query)
      .then((recommendations: SearchRecommendation[]) => {
        if (!recommendations || recommendations.length < 1) {
          clearRecommendations();
          return;
        }
        element &&
          element.replaceChildren(
            <>
              <SearchRecommendations
                query={query}
                recommendations={recommendations}
              />
            </>
          );
      })
      .catch((error) => {
        clearRecommendations();
        if (error.name === "AbortError") {
          Log.error(
            `Search recommendation request timed out. Search Query: ${query}`
          );
        } else {
          Log.error(error);
        }
      });
  }, this.debounceDelay);
}

class MobileSearchBar extends SearchBarBase {
  constructor(config: Config) {
    super(config);
  }

  show = () => {
    this.searchBar?.showModal();
    this.searchBarInput?.focus();
  };

  close = () => {
    if (this.searchBar?.open) this.searchBar?.close();
  };

  handleBackdropClick = (event: Event) => {
    if (event.target === event.currentTarget) this.close();
  };

  handleCategorySelection = () => {
    const sideMenuSettings: SideMenuSettings = {
      position: "left",
      disableScroll: false,
    };

    this.close();
    openSideMenu(<CategoryMenu />, sideMenuSettings);
  };

  init() {
    select(this.config.openSearchBarClass)?.addEventListener(
      "click",
      this.show
    );
    select(this.config.openCategorySelectionClass)?.addEventListener(
      "click",
      this.handleCategorySelection
    );
    select(this.config.closeSearchBarClass)?.addEventListener(
      "click",
      this.close
    );
    select(this.config.searchBarFormClass)?.addEventListener(
      "submit",
      this.handleFormSubmit
    );
    this.searchBar?.addEventListener("mousedown", this.handleBackdropClick);

    if (isInABTest("SearchRecommendation", "B")) {
      this.searchBarInput?.addEventListener(
        "input",
        this.handleSearchRecommendations
      );
    }
  }

  cleanup() {
    if (this.searchBar?.open) this.close();

    select(this.config.openSearchBarClass)?.removeEventListener(
      "click",
      this.show
    );
    select(this.config.openCategorySelectionClass)?.removeEventListener(
      "click",
      this.handleCategorySelection
    );
    select(this.config.closeSearchBarClass)?.removeEventListener(
      "click",
      this.close
    );
    this.searchBar?.removeEventListener("mousedown", this.handleBackdropClick);

    if (isInABTest("SearchRecommendation", "B")) {
      this.searchBarInput?.removeEventListener(
        "input",
        this.handleSearchRecommendations
      );
    }
  }
}

class DesktopSearchBar extends SearchBarBase {
  constructor(config: Config) {
    super(config);
  }

  show = () => {
    if (this.searchBar?.classList.contains("has-focus")) return;

    this.searchBar?.classList.add("has-focus");
    showBackdrop();
    this.searchBarInput?.focus();
  };

  close = () => {
    if (!this.searchBar?.classList.contains("has-focus")) return;

    this.searchBar?.classList.remove("has-focus");
    hideBackdrop();
  };

  handleCategorySelection = () => {
    const sideMenuSettings: SideMenuSettings = {
      position: "left",
      disableScroll: false,
    };

    this.close();
    openSideMenu(<CategoryMenu />, sideMenuSettings);
  };

  handleCategorySelectionKeyboard = (event: Event) => {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.key === "Enter" || keyboardEvent.key === " ") {
      event.preventDefault();

      this.handleCategorySelection();
    }
  };

  handleFocusIn = (event: FocusEvent) => {
    if (this.searchBar?.contains(event.target as Node)) {
      this.show();
    }
  };

  handleFocusOut = (event: FocusEvent) => {
    // We add a delay here to stop a bug in Mac Safari where the focusout event closes the searchbar
    // before the click event fires, preventing the click from working
    setTimeout(() => {
      if (!this.searchBar?.contains(event.relatedTarget as Node)) {
        this.close();
      }
    }, 250);
  };

  init() {
    this.searchBar?.addEventListener("focusin", this.handleFocusIn);
    this.searchBar?.addEventListener("focusout", this.handleFocusOut);

    select(this.config.openCategorySelectionClass)?.addEventListener(
      "click",
      this.handleCategorySelection
    );

    // Add the keydown event for keyboard support when selecting "Shop by departments"
    select(this.config.openCategorySelectionClass)?.addEventListener(
      "keydown",
      this.handleCategorySelectionKeyboard
    );

    select(this.config.searchBarFormClass)?.addEventListener(
      "submit",
      this.handleFormSubmit
    );

    if (isInABTest("SearchRecommendation", "B")) {
      this.searchBarInput?.addEventListener(
        "input",
        this.handleSearchRecommendations
      );
    }
  }

  cleanup() {
    this.close();

    this.searchBar?.removeEventListener("focusin", this.handleFocusIn);
    this.searchBar?.removeEventListener("focusout", this.handleFocusOut);

    select(this.config.openCategorySelectionClass)?.removeEventListener(
      "click",
      this.handleCategorySelection
    );
    select(this.config.openCategorySelectionClass)?.removeEventListener(
      "keydown",
      this.handleCategorySelectionKeyboard
    );

    select(this.config.searchBarFormClass)?.removeEventListener(
      "submit",
      this.handleFormSubmit
    );

    if (isInABTest("SearchRecommendation", "B")) {
      this.searchBarInput?.removeEventListener(
        "input",
        this.handleSearchRecommendations
      );
    }
  }
}

export function initSearchBar() {
  const config: Config = {
    searchBarClass: ".js-search-bar",
    openSearchBarClass: ".js-open-search-bar",
    closeSearchBarClass: ".js-close-search-bar",
    searchBarInputClass: ".js-search-bar-input",
    searchBarFormClass: ".js-search-bar-form",
    openCategorySelectionClass: ".js-open-category-selection",
    searchRecommendationsClass: ".js-search-recommendations",
    debounceDelay: 280,
    minLengthForSearch: 2,
    minLengthForSearchRecommendations: 3,
  };

  const mobileSearchBar = new MobileSearchBar(config);
  const desktopSearchBar = new DesktopSearchBar(config);

  minWidth(
    DeviceWidth.Medium,
    () => {
      mobileSearchBar.cleanup();
      desktopSearchBar.init();
    },
    () => {
      desktopSearchBar.cleanup();
      mobileSearchBar.init();
    }
  );
}
